import React, { useState } from "react";
import { Award, Users, TrendingUp, Coins, Trophy, Gift, Heart, MessageCircle, Clock, Wallet, PieChart as PieIcon, CalendarClock, MessageSquareText, Layers } from "lucide-react";
import { useTranslation, useFormat } from "../../../i18n";
import { useAnalyticsSection } from "../../analytics/hooks";
import { KpiTile, ChartCard, Segmented, Skeleton, ErrorState, StatRow } from "../../analytics/primitives";
import { TrendChart, StackedTrendChart, DonutChart, BarList, WeekHourHeatmap, Histogram } from "../../analytics/charts";
import InsightsPanel from "../../analytics/InsightsPanel";
import { SERIES } from "../../analytics/palette";

const OverviewTab = ({ filters, options, onNavigate }) => {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  const overview = useAnalyticsSection("overview", filters);
  const insights = useAnalyticsSection("insights", filters);
  const series = useAnalyticsSection("timeseries", filters);
  const breakdowns = useAnalyticsSection("breakdowns", filters);
  const [volumeMetric, setVolumeMetric] = useState("recognitions");
  const [volumeMode, setVolumeMode] = useState("bar");
  const [typeMode, setTypeMode] = useState("donut");

  if (overview.isError) return <ErrorState error={overview.error} onRetry={overview.refetch} />;
  const ov = overview.data?.overview;
  const scope = overview.data?.scope;
  const granularity = series.data?.scope?.granularity || scope?.granularity;
  const pctFmt = (v) => (v === null || v === undefined ? "—" : fmt.number(v / 100, { style: "percent", maximumFractionDigits: 0 }));

  const volumeSeries = {
    recognitions: [{ key: "recognitions", label: t("series.recognitions"), color: SERIES[0], kind: volumeMode }],
    points: [{ key: "points", label: t("series.points"), color: SERIES[3], kind: volumeMode }],
    people: [
      { key: "unique_senders", label: t("series.unique_senders"), color: SERIES[1], kind: "line" },
      { key: "unique_recipients", label: t("series.unique_recipients"), color: SERIES[2], kind: "line" },
    ],
  }[volumeMetric];

  const heatmapWeekStart = options?.organization?.week_start_day ?? 0;

  return (
    <div className="space-y-4">
      {/* Headline KPIs */}
      {overview.isLoading || !ov ? (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} height={92} />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <KpiTile icon={Award} label={t("kpi.recognitions")} value={fmt.number(ov.recognitions.value)} metric={ov.recognitions} tone="purple" sub={ov.recognitions_per_member !== null ? t("kpi.per_member", { value: fmt.number(ov.recognitions_per_member, { maximumFractionDigits: 1 }) }) : undefined} />
            <KpiTile icon={Users} label={t("kpi.participation_rate")} value={pctFmt(ov.participation_rate.value)} metric={{ ...ov.participation_rate, delta_pct: ov.participation_rate.delta }} tone="blue" progress={ov.participation_rate.value} sub={t("kpi.of_members", { participants: fmt.number(ov.participants.value), headcount: fmt.number(ov.headcount) })} hint={t("kpi.participation_hint")} />
            <KpiTile icon={TrendingUp} label={t("kpi.unique_senders")} value={fmt.number(ov.unique_senders.value)} metric={ov.unique_senders} tone="teal" sub={t("kpi.rate_of_members", { rate: pctFmt(ov.sender_rate) })} />
            <KpiTile icon={Heart} label={t("kpi.unique_recipients")} value={fmt.number(ov.unique_recipients.value)} metric={ov.unique_recipients} tone="pink" sub={t("kpi.rate_of_members", { rate: pctFmt(ov.recipient_rate) })} />
            <KpiTile icon={Coins} label={t("kpi.points_awarded")} value={fmt.number(ov.points_awarded.value)} metric={ov.points_awarded} tone="green" sub={ov.points_pending ? t("kpi.pending_points", { value: fmt.number(ov.points_pending) }) : t("kpi.avg_points", { value: fmt.number(ov.avg_points.value, { maximumFractionDigits: 1 }) })} />
            <KpiTile icon={Wallet} label={t("kpi.budget_used")} value={pctFmt(ov.budget?.utilisation)} progress={ov.budget?.utilisation || 0} tone="yellow" sub={t("kpi.budget_of", { used: fmt.currency(ov.budget?.used || 0, "USD"), allocated: fmt.currency(ov.budget?.allocated || 0, "USD") })} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <KpiTile icon={Trophy} label={t("kpi.nominations")} value={fmt.number(ov.nominations.value)} metric={ov.nominations} tone="yellow" sub={t("kpi.nominators", { count: ov.nominators })} />
            <KpiTile icon={Gift} label={t("kpi.redemptions")} value={fmt.number(ov.redemptions.value)} metric={ov.redemptions} tone="pink" sub={t("kpi.points_redeemed", { value: fmt.number(ov.points_redeemed.value, { maximumFractionDigits: 0 }) })} />
            <KpiTile icon={Heart} label={t("kpi.reactions")} value={fmt.number(ov.reactions.value)} metric={ov.reactions} tone="red" />
            <KpiTile icon={MessageCircle} label={t("kpi.comments")} value={fmt.number(ov.comments.value)} metric={ov.comments} tone="blue" />
            <KpiTile icon={Clock} label={t("kpi.pending_approvals")} value={fmt.number(ov.approvals.pending)} tone={ov.approvals.pending > 0 ? "red" : "slate"} sub={t("kpi.approval_rate", { rate: pctFmt(ov.approvals.approval_rate) })} />
            <KpiTile icon={Wallet} label={t("kpi.outstanding_points")} value={fmt.number(ov.liability.total_points)} tone="slate" sub={t("kpi.in_wallets", { count: ov.liability.wallets_with_balance, value: fmt.currency(ov.liability.total_value || 0, "USD") })} hint={t("kpi.liability_hint")} />
          </div>
        </>
      )}

      {/* Insights */}
      <section aria-label={t("insights.title")}>
        <h2 className="text-sm font-semibold text-slate-700 mb-2">{t("insights.title")}</h2>
        {insights.isLoading ? <Skeleton height={64} /> : <InsightsPanel insights={insights.data?.insights} onNavigate={onNavigate} />}
      </section>

      {/* Volume + type mix */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard
          title={t("volume_title")}
          icon={CalendarClock}
          subtitle={scope ? t("scope_summary", { days: scope.days, granularity: t(`granularity.${granularity || "day"}`) }) : undefined}
          actions={
            <>
              <Segmented ariaLabel={t("volume_metric")} value={volumeMetric} onChange={setVolumeMetric} options={[{ value: "recognitions", label: t("series.recognitions") }, { value: "points", label: t("series.points") }, { value: "people", label: t("series.people") }]} />
              {volumeMetric !== "people" && <Segmented ariaLabel={t("chart_mode")} value={volumeMode} onChange={setVolumeMode} options={[{ value: "bar", label: t("chart_modes.bar") }, { value: "area", label: t("chart_modes.area") }]} />}
            </>
          }
          table={
            series.data
              ? {
                  columns: [
                    { key: "bucket", label: t("table.period") },
                    { key: "recognitions", label: t("series.recognitions"), align: "right" },
                    { key: "points", label: t("series.points"), align: "right" },
                    { key: "unique_senders", label: t("series.unique_senders"), align: "right" },
                    { key: "unique_recipients", label: t("series.unique_recipients"), align: "right" },
                  ],
                  rows: series.data.rows,
                  keyField: "bucket",
                }
              : undefined
          }
        >
          {series.isLoading ? <Skeleton /> : <TrendChart rows={series.data?.rows} previous={filters.compare !== false && volumeMetric !== "people" ? series.data?.previous : null} series={volumeSeries} granularity={granularity} />}
        </ChartCard>

        <ChartCard
          title={t("type_breakdown_title")}
          icon={PieIcon}
          actions={<Segmented ariaLabel={t("chart_mode")} value={typeMode} onChange={setTypeMode} options={[{ value: "donut", label: t("chart_modes.donut") }, { value: "stacked", label: t("chart_modes.stacked") }]} />}
          table={
            breakdowns.data
              ? {
                  columns: [
                    { key: "name", label: t("table.type") },
                    { key: "count", label: t("table.count"), align: "right" },
                    { key: "share", label: t("table.share"), align: "right", render: (r) => pctFmt(r.share) },
                    { key: "points", label: t("table.points"), align: "right" },
                    { key: "unique_recipients", label: t("table.recipients"), align: "right" },
                  ],
                  rows: breakdowns.data.by_type,
                  keyField: "name",
                }
              : undefined
          }
        >
          {breakdowns.isLoading ? (
            <Skeleton />
          ) : typeMode === "donut" ? (
            <DonutChart data={breakdowns.data?.by_type} centerLabel={t("series.recognitions")} />
          ) : (
            <StackedTrendChart rows={breakdowns.data?.by_type_over_time?.rows} keys={breakdowns.data?.by_type_over_time?.series} granularity={granularity} />
          )}
        </ChartCard>
      </div>

      {/* Who / where / when */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title={t("by_department_title")} icon={Layers} subtitle={t("by_department_subtitle")}>
          {breakdowns.isLoading ? <Skeleton height={200} /> : <BarList data={breakdowns.data?.by_department} shareKey="share" />}
        </ChartCard>
        <ChartCard title={t("by_location_title")} icon={Layers}>
          {breakdowns.isLoading ? <Skeleton height={200} /> : <BarList data={breakdowns.data?.by_location} shareKey="share" color={SERIES[3]} />}
        </ChartCard>
        <ChartCard title={t("mix_title")} icon={Layers}>
          {breakdowns.isLoading || !ov ? (
            <Skeleton height={200} />
          ) : (
            <div>
              <StatRow label={t("mix.with_points")} value={pctFmt(ov.points_share)} />
              <StatRow label={t("mix.team_wide")} value={pctFmt(ov.team_wide_share)} />
              <StatRow label={t("mix.private")} value={pctFmt(ov.private_share)} />
              {(breakdowns.data?.by_visibility || []).map((v) => (
                <StatRow key={v.name} label={t("mix.visibility", { name: t(`visibility.${v.name}`, { defaultValue: v.name }) })} value={`${fmt.number(v.count)} · ${pctFmt(v.share)}`} />
              ))}
              {(breakdowns.data?.by_source || []).map((v) => (
                <StatRow key={v.name} label={t("mix.source", { name: t(`source.${v.name}`, { defaultValue: v.name }) })} value={`${fmt.number(v.count)} · ${pctFmt(v.share)}`} />
              ))}
            </div>
          )}
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title={t("heatmap.title")} icon={CalendarClock} subtitle={t("heatmap.subtitle", { tz: options?.organization?.timezone || "UTC" })} className="lg:col-span-2">
          {breakdowns.isLoading ? <Skeleton height={200} /> : <WeekHourHeatmap heatmap={breakdowns.data?.heatmap} weekStartDay={heatmapWeekStart} />}
        </ChartCard>
        <ChartCard title={t("messages.title")} icon={MessageSquareText} subtitle={t("messages.subtitle")}>
          {breakdowns.isLoading ? (
            <Skeleton height={200} />
          ) : (
            <div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div className="rounded-xl bg-slate-50 p-2">
                  <p className="text-[10px] text-slate-500">{t("messages.avg_length")}</p>
                  <p className="text-lg font-bold text-slate-800">{breakdowns.data?.messages?.avg_length ? fmt.number(breakdowns.data.messages.avg_length, { maximumFractionDigits: 0 }) : "—"}</p>
                </div>
                <div className="rounded-xl bg-slate-50 p-2">
                  <p className="text-[10px] text-slate-500">{t("messages.median_length")}</p>
                  <p className="text-lg font-bold text-slate-800">{breakdowns.data?.messages?.median_length ? fmt.number(breakdowns.data.messages.median_length, { maximumFractionDigits: 0 }) : "—"}</p>
                </div>
              </div>
              <Histogram data={breakdowns.data?.messages?.buckets || []} labelKey="key" valueKey="count" height={150} formatLabel={(d) => t(`messages.bucket.${d.key}`)} color={SERIES[2]} />
            </div>
          )}
        </ChartCard>
      </div>
    </div>
  );
};

export default OverviewTab;
